import { combineReducers } from "redux";
import { configureStore } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";

import checkoutReducer from "./slices/checkout";
import serverContextReducer from "./slices/serverContext";
import plansReducer from "./slices/plans";
import recommendedPlanReducer from "./slices/recommendedPlan";
import surveyAnswersReducer from "./slices/surveyAnswers";
import trialReducer from "./slices/trial";
import surveyNameSpaceReducer from "./slices/surveyNameSpace";
import languageReducer from "./slices/language";
import userDataReducer from "./slices/userData";
import visitorStatusReducer from "./slices/visitorStatus";
import paymentEnrollmentFormReducer from "./slices/paymentEnrollmentForm";
import geoLocationReducer from "./slices/geoLocation";
import routeIdReducer from "./slices/routeId";
import startEventsReducer from "./slices/startEvents";
import linearBuyflowReducer from "./slices/linearBuyflow";
import activeSubscriptionDataReducer from "./slices/activeSubscriptionData";
import upidReducer from "./slices/upid";

import { rehydrateStore } from "./persistence";
import programSwitchErrorDetailsReducer from "./slices/programSwitchErrorDetails";

// Inlined this vs. having in a separate file to avoid a circular dependency
export const rootReducer = combineReducers({
  serverContext: serverContextReducer,
  checkout: checkoutReducer,
  plans: plansReducer,
  recommendedPlan: recommendedPlanReducer,
  surveyAnswers: surveyAnswersReducer,
  trial: trialReducer,
  userData: userDataReducer,
  surveyNameSpace: surveyNameSpaceReducer,
  language: languageReducer,
  visitorStatus: visitorStatusReducer,
  paymentEnrollmentForm: paymentEnrollmentFormReducer,
  geoLocation: geoLocationReducer,
  routeId: routeIdReducer,
  startEvents: startEventsReducer,
  linearBuyflow: linearBuyflowReducer,
  activeSubscriptionData: activeSubscriptionDataReducer,
  upid: upidReducer,
  programSwitchErrorDetails: programSwitchErrorDetailsReducer,
});

export type ReducerStore = ReturnType<typeof rootReducer>;

const store = configureStore({
  reducer: rootReducer,
  devTools: true,
  preloadedState: rehydrateStore(),
});

/**
 * Attaches a change listener to the store.
 * @param select return state derived from store
 * @param onChange change listener callback
 */
export function observeStore<T>(
  select: (state: CoreReduxState) => T,
  onChange: (value: T) => void
) {
  let currentState = select(store.getState());

  const handleChange = () => {
    const nextState = select(store.getState());
    if (!isEqual(nextState, currentState)) {
      currentState = nextState;
      onChange(currentState);
    }
  };

  return store.subscribe(handleChange);
}

export type CoreReduxState = ReturnType<typeof store.getState>;
export type GetAppState = typeof store.getState;
export type AppDispatch = typeof store.dispatch;

export default store;
